import type { ApiPPG, Change, PPGRow } from '../../../types/promoMetrics';

export function initializeFromApi(apiPpgs: ApiPPG[]): {
    rows: PPGRow[];
    snapshot: Map<string, Record<string, number>>;
} {
    const snapshot = new Map<string, Record<string, number>>();

    const rows: PPGRow[] = apiPpgs.map((ppg) => {
        const values: Record<string, number> = {};
        const recommended: Record<string, number | null> = {};

        for (const [key, mv] of Object.entries(ppg.metrics ?? {})) {
            values[key] = mv.input;
            recommended[key] = mv.recommended;
        }
        snapshot.set(String(ppg.ppg_id), { ...values });

        const children = ppg.products?.map((p) => {
            const childValues: Record<string, number> = {};
            const childRecommended: Record<string, number | null> = {};
            for (const [key, mv] of Object.entries(p.metrics ?? {})) {
                childValues[key] = mv.input;
                childRecommended[key] = mv.recommended;
            }
            return {
                ppg_id: p.product_id,
                ppg_name: p.product_name,
                values: childValues,
                recommended: childRecommended,
                userEditedKeys: new Set<string>(),
                lastCascadedKeys: new Set<string>(),
            };
        });

        return {
            ppg_id: ppg.ppg_id,
            ppg_name: ppg.ppg_name,
            values,
            recommended,
            children,
            userEditedKeys: new Set<string>(),
            lastCascadedKeys: new Set<string>(),
        };
    });

    return { rows, snapshot };
}

export function buildChangesPayload(ppgData: PPGRow[], baseline: Map<string, Record<string, number>>): Change[] {
    const changes: Change[] = [];

    for (const ppg of ppgData) {
        const ppgId = String(ppg.ppg_id);
        const original = baseline.get(ppgId);
        if (!original) continue;

        for (const metricKey of ppg.userEditedKeys) {
            const currentValue = ppg.values[metricKey];
            if (currentValue === undefined) continue;
            const oldValue = original[metricKey] ?? null;
            if (oldValue !== currentValue) {
                changes.push({ ppg_id: ppgId, metric_key: metricKey, value: currentValue });
            }
        }
    }
    return changes;
}

/**
 * Required-fields gate driven by engine-derived field states: a field counts when
 * it is required AND currently visible AND not disabled. `fieldStates` comes from
 * `deriveFieldStatesForScope('promo', ...)` (keyed by bare field name).
 */
export function areRequiredFieldsFilled(
    fieldStates: Record<string, { visible: boolean; disabled: boolean; required: boolean }>,
    formValues: Record<string, unknown>,
): boolean {
    return Object.entries(fieldStates)
        .filter(([, state]) => state.required && state.visible && !state.disabled)
        .every(([name]) => {
            const v = formValues[name];
            return v !== undefined && v !== null && v !== '';
        });
}
